import React, { createContext, useContext, useState, FC, useEffect } from 'react'
import { userService } from '../services'
import { AuthenticatedUser, UserSelfUpdateFormValues } from '../types'

interface AuthContextProps {
	authenticating: boolean
	user: AuthenticatedUser
	userHasManyServiceProviders: boolean
	userHasPermission(permissions: string[]): boolean
	updateSelf(values: UserSelfUpdateFormValues): Promise<void>
	userIsResellerAdmin: boolean
	logout(): Promise<void>
}

interface AuthProviderProps {
	children: React.ReactChild
}

const AuthProvider: FC<AuthProviderProps> = props => {
	const [authenticating, setAuthenticating] = useState<boolean>(true)
	const [user, setUser] = useState<AuthenticatedUser>(getInitialState())

	const logout = async () => {
		const { data } = await userService.logout()
		window.sessionStorage.clear()
		window.localStorage.removeItem('user')
		window.location.assign(data.url)
	}

	const updateSelf = async (values: UserSelfUpdateFormValues) => {
		if (!user?.is_b2c) {
			return
		}

		await userService.updateSelf(user.id, {
			email: values.email,
			name: values.name,
			password: values.password ?? undefined // We only want to send password if it was updated
		})

		setUser(user => ({ ...user, ...values }))
	}

	const userHasPermission = (permissions: string[]): boolean => {
		return user.permissions.filter(({ name }) => permissions.includes(name)).length > 0
	}

	const userIsResellerAdmin = user?.organization_type === 'reseller' && user?.resellers?.length > 0

	useEffect(() => {
		const start = async (): Promise<void> => {
			try {
				const { data } = await userService.getSelf()
				setUser(data)
				setAuthenticating(false)
			} catch (error) {
				window.location.assign(userService.loginUrl(`/${window.location.hash}`))
			}
		}

		start()
	}, [])

	return (
		<AuthContext.Provider
			value={{
				user,
				userHasManyServiceProviders: user?.service_providers?.length > 1,
				userHasPermission,
				authenticating,
				logout,
				updateSelf,
				userIsResellerAdmin
			}}
			{...props}
		/>
	)
}

const AuthContext = createContext<AuthContextProps>(null)

const useAuth = (): AuthContextProps => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within a AuthProvider')
	}
	return context
}

/**
 * We use `getInitialState` for our initial `user` state. Replace me with your real implementation.
 * You might opt to get this from the session, a JWT etc...
 * We're using local storage to demonstrate authentication in your app
 */
const getInitialState = (): AuthenticatedUser => {
	const storedUser = window.localStorage.getItem('user')
	return storedUser ? JSON.parse(storedUser) : null
}

export { AuthProvider, useAuth }
